'use client'

/**
 * ChangePasswordDialog — current/new/confirm password change.
 *
 * Used in two places (task 1213):
 *   • Team detail screen — user changes their own password (asks for current).
 *   • First login with a temp password — `lockCurrent` prefills the temp
 *     password they just used and hides the Current field (no re-typing).
 *
 * Policy (enforced client + server): ≥8 chars, a number, and a symbol.
 * Calls POST /api/mvp-auth/change-password.
 */
import { useState, useEffect } from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'

import CustomTextField from '@core/components/mui/TextField'
import { api } from '@/lib/api'
import { toast } from 'react-toastify'

export function passwordPolicyError(pw: string): string | null {
  if (!pw || pw.length < 8) return 'Password must be at least 8 characters long.'
  if (!/[0-9]/.test(pw)) return 'Password must include at least one number.'
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must include at least one symbol.'
  return null
}

interface Props {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  /** Prefill + hide the Current field (first-login temp-password flow). */
  lockCurrent?: string
  title?: string
  /** Hide the cancel/close affordance (force the user to set a password). */
  mandatory?: boolean
}

export default function ChangePasswordDialog({ open, onClose, onSuccess, lockCurrent, title, mandatory }: Props) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setCurrent(lockCurrent ?? '')
      setNext(''); setConfirm(''); setError(null); setShow(false)
    }
  }, [open, lockCurrent])

  const submit = async () => {
    setError(null)
    const cur = lockCurrent ?? current
    if (!cur) { setError('Current Password Needed'); return }
    const policy = passwordPolicyError(next)
    if (policy) { setError(policy); return }
    if (next !== confirm) { setError('Matching Passwords Needed'); return }

    setBusy(true)
    try {
      await api.post('/api/mvp-auth/change-password', { currentPassword: cur, newPassword: next })
      toast.success('Password changed successfully.')
      onSuccess?.()
      onClose()
    } catch (e: any) {
      setError(e?.message || "Couldn't Change Password")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={mandatory ? undefined : onClose} maxWidth='xs' fullWidth disableEscapeKeyDown={mandatory}>
      <DialogTitle>{title ?? 'Change Password'}</DialogTitle>
      <DialogContent>
        {mandatory && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            For your security, please set a new password to continue.
          </Typography>
        )}
        {error && <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!lockCurrent && (
            <CustomTextField
              fullWidth label='Current password' type={show ? 'text' : 'password'}
              value={current} onChange={e => setCurrent(e.target.value)} autoComplete='current-password'
            />
          )}
          <CustomTextField
            fullWidth label='New password' type={show ? 'text' : 'password'}
            value={next} onChange={e => setNext(e.target.value)} autoComplete='new-password'
            helperText='At least 8 characters, one number, and one symbol.'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton size='small' edge='end' onClick={() => setShow(s => !s)}>
                    <i className={show ? 'tabler-eye-off' : 'tabler-eye'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <CustomTextField
            fullWidth label='Confirm new password' type={show ? 'text' : 'password'}
            value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete='new-password'
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {!mandatory && <Button color='secondary' onClick={onClose} disabled={busy}>Cancel</Button>}
        <Button variant='contained' onClick={submit} disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color='inherit' /> : undefined}>
          {busy ? 'Saving…' : 'Set Password'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
